import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useActivityStore } from '../../store/useActivityStore';
import { logDebug, logError } from '../../services/loggerService';

/**
 * Renderless bridge between pushService and react-router. pushService runs
 * outside the router tree and cannot call useHistory(), so on a tap it stashes
 * the notification's `data.targetUrl` in useActivityStore.pendingPushRoute;
 * this component consumes that intent and navigates.
 *
 * Same-origin URLs route via history.push() so SecretLoginHandler can pick up
 * any `?s=<secret>` carried in the URL (logged-out users tapping a push must
 * still complete the secret-login handshake). Cross-origin URLs open
 * externally.
 */
export const PushTapNavigator: React.FC = () => {
  const history = useHistory();
  const pendingPushRoute = useActivityStore((s) => s.pendingPushRoute);
  const setPendingPushRoute = useActivityStore((s) => s.setPendingPushRoute);

  useEffect(() => {
    if (!pendingPushRoute) return;

    const url = pendingPushRoute;
    setPendingPushRoute(null);

    // BE sends relative paths (e.g. `/orders/abc?s=xyz`). Treat anything
    // starting with `/` as same-origin and route via history.push so
    // SecretLoginHandler can pick up `?s=` from the search string.
    if (url.startsWith('/')) {
      logDebug('PushTapNavigator: same-origin relative navigation', undefined, { target: url });
      history.push(url);
      return;
    }

    try {
      const parsed = new URL(url);
      if (parsed.origin === window.location.origin) {
        const target = parsed.pathname + parsed.search + parsed.hash;
        logDebug('PushTapNavigator: same-origin navigation', undefined, { target });
        history.push(target);
      } else {
        logDebug('PushTapNavigator: external navigation', undefined, { url });
        window.open(url, '_blank');
      }
    } catch (err) {
      logError(
        err instanceof Error ? err : new Error(String(err)),
        'PushTapNavigator: invalid targetUrl',
        undefined,
        { url },
      );
    }
  }, [pendingPushRoute, history, setPendingPushRoute]);

  return null;
};
